import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CommonService } from "../../services/CommonService";
import { DDLDataTable } from "../DDLDataTable";
import { NewsInfoSetupFieldsPanel } from "./Panels/NewsInfoSetupFieldsPanel";
import { NewsSetupValidationSchema } from "./validator";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { INews } from "../../models/NewsSetup";
import { IGetListRequest } from "../../models";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { formatDate, formatDateToShow, useNavigateToLogout } from "../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";
import { NewsSetupToolbar } from "./NewsSetupToolbar";






export interface INewsSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "news-single-data-record";
const NEWS_LIST_DATA_QUERY_KEY = "news-list";

const useGetNewsData = (
	// clientId: string,
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: () => {
			const url = id && `/v1/news-setup/${id}/`
			return CommonService.getById(url, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useNews = (
	// clientId: string,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [NEWS_LIST_DATA_QUERY_KEY, params],
		queryFn: (data) => {
			const url = `/v1/news-setup/?order_by=${params.order_by}&order_direction=${params.order_direction}&page=${params.page}&size=${params.size}&search_query=${params.search_query}`
			return CommonService.getList(
				url,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

export const NewsSetup: React.FC<INewsSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [newsId, setNewsId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "date",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "date",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const formMethods = useForm<any>({
		resolver: yupResolver(NewsSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetNewsData(
		editRecordId,
		navigateToLogout
	);

	const { isPending, data } = useNews(
		queryParams,
		navigateToLogout
	);
	const newsList: any | null = data;

	const updatedNewsListItems = useMemo(() => {
		return (
			newsList &&
			newsList.items &&
			newsList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/news/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.date}
					</Link>
				),
				date: formatDateToShow(item.date),
			}))
		);
	}, [newsList]);

	const { isPending: isSubmitting, mutate: submitNewsFormFields } =
		useMutation({
			mutationFn: (variables: any) =>
				CommonService.create(
					variables.createUrl,
					variables.updateUrl,
					variables.payload,
					variables.navigateToLogout,
				),
			onSuccess: (res: IServerError | IResponse | any) => {

				if (res.status === 200) {

					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/news/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [NEWS_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {

					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/news/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [NEWS_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const onSubmit = (values: any) => {
		const payload = {
			id: id && id,
			navigateToLogout: navigateToLogout,
			date: formatDate(values.date),
			description: values.description,
		};
		const ApiCredentials = {
			createUrl: `/v1/news-setup/`,
			updateUrl: id && `/v1/news-setup/${id}/`,
			payload: payload,
			navigateToLogout: navigateToLogout,
		}
		submitNewsFormFields(ApiCredentials);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteNewsById } = useMutation({
		mutationFn: (values: any) => CommonService.deleteById(
			values.url,
			values.navigateToLogout
		),

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {

				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/news`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [NEWS_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteNewsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			// let delPayload = {
			// 	newsId: newsId,
			// 	navigateToLogout: navigateToLogout,
			// };			
			let deleteApiCredentials = {
				url: `/v1/news-setup/${newsId}/`,
				navigateToLogout: navigateToLogout,
			}
			deleteNewsById(deleteApiCredentials);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setNewsId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setNewsId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveNewsById } = useMutation({
		mutationFn: (values: any) => CommonService.archive(
			values.url,
			values.navigateToLogout
		),

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {

				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/news`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [NEWS_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveNewsField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				newsId: newsId,
				navigateToLogout: navigateToLogout,
			};
			let archiveApiCredentials = {
				url: `/v1/news-setup/`,
				navigateToLogout: navigateToLogout,
			}
			archiveNewsById(archiveApiCredentials);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setNewsId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/news`;
		navigate(path);
	};

	const expandedLabels = ["Misc.", "News"];
	const expandedNewsOrSetupItem = "News"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "News" &&
				item.label === expandedNewsOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedNewsOrSetupItem && "/news",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		NewsName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback((params: INews | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/news/${params.value}`;
		navigate(path);
	}, []);

	return (
		<Grid>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive}
				testId="news Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteNewsField}
				cancelActionHandler={onCloseHandler}
				testId="news-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 5, lg: 4.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="News"
										testId={`news`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<NewsInfoSetupFieldsPanel />
									</StandardPanel>
								}
								toolbar={
									<NewsSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 2, lg: 3.4, xl: 5.4 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 5, lg: 4.1, xl: 3.1 }}
							sx={{ marginTop: "16px", display: { xs: "none", sm: "none", md: "block" } }}
						>
							<DDLDataTable
								rows={
									newsList && updatedNewsListItems
										? updatedNewsListItems
										: []
								}
								columns={[
									{ field: "label", headerName: "Date" },
									{ field: "description", headerName: "Description" },
								]}
								showHeaderColumns={true}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								showArchiveIcon={false}
								isLoading={isPending}
								totalRecords={
									newsList && newsList.total
										? newsList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveNewsField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"news-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
